import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../reducers";
// Imported from the concrete modules rather than the utils barrel: the barrel
// re-exports ApiUtils, which would drag the axios interceptor setup (and the
// store) into this globally mounted component's module graph.
import { ObjectUtils } from "../../utils/ObjectUtils";

/**
 * Grace period before the "finished" message is announced. Requests are often
 * chained (one thunk firing the next), which drops isLoading for a few
 * milliseconds in between; waiting stops the live region ping-ponging between
 * "started" and "finished" for what the user experiences as a single wait.
 */
const COMPLETION_SETTLE_MS = 400;

/**
 * How long the "finished" message stays in the region before it is emptied
 * again, so the region returns to its idle empty state. Emptying a live region
 * is silent, so this only controls how long the text is exposed to assistive
 * technology, not how long the user hears it.
 */
const COMPLETION_CLEAR_MS = 3000;

export const Spinner = () => {
  const { isLoading } = useSelector((state: RootState) => state.status);
  const { contentFooter } = useSelector((state: RootState) => state.common);
  const content = contentFooter?.contentResource?.contents;
  const loadingText = ObjectUtils.extractLabelFromObject(content, "lblLoading")?.display;
  const completeText = ObjectUtils.extractLabelFromObject(content, "lblLoadingComplete")?.display;
  const [announcement, setAnnouncement] = useState("");
  // Kept in a ref so the announcement effect depends only on isLoading: a
  // content-resource or locale update must not restart the announcement cycle.
  const textRef = useRef({ loadingText, completeText });
  textRef.current = { loadingText, completeText };
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const wasLoadingRef = useRef(false);

  /**
   * WCAG 2.1 AA - 4.1.3 Status Messages (FLEXS-14877).
   *
   * The visual overlay is decorative, so both edges of the busy state have to
   * be spoken. Announcing only the start is not enough: emptying a live region
   * produces no announcement, so a screen reader user would hear "Processing,
   * please wait" followed by silence, which is indistinguishable from the
   * application having hung. The completion message is therefore pushed into
   * the same region once loading settles, then cleared so the region returns
   * to its idle empty state.
   */
  useEffect(() => {
    if (isLoading) {
      wasLoadingRef.current = true;
      setAnnouncement(textRef.current.loadingText);
    } else if (wasLoadingRef.current) {
      wasLoadingRef.current = false;
      timersRef.current.push(
        setTimeout(() => {
          setAnnouncement(textRef.current.completeText);
          timersRef.current.push(setTimeout(() => setAnnouncement(""), COMPLETION_CLEAR_MS));
        }, COMPLETION_SETTLE_MS)
      );
    }

    // Runs before the next transition and on unmount: a load starting again
    // cancels a pending "finished" message rather than queueing both.
    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
  }, [isLoading]);

  /**
   * The live region is rendered unconditionally - it must already exist in the
   * DOM before its text changes, otherwise assistive technology never picks
   * the update up - and is empty whenever nothing is happening.
   *
   * The loader is global (mounted once in App.tsx) and driven by the axios
   * interceptors in ApiUtils, so this covers every loading indicator in the
   * app, including the "Cancel" flow on Your Enrollment Summary reported in
   * the finding. The pending-request counter in the interceptor means a burst
   * of parallel calls flips isLoading once, so a page load announces once
   * rather than once per request.
   */
  return (
    <>
      <div className="visuallyhidden" role="status" aria-live="polite" aria-atomic="true">
        {announcement}
      </div>
      {isLoading ? (
        <div className="loaderwrapper" aria-hidden="true">
          <div className="loader">
            <div className="dot primarycolor"></div>
            <div className="dot primarycolor"></div>
            <div className="dot primarycolor"></div>
            <div className="dot primarycolor"></div>
            <div className="dot primarycolor"></div>
            <div className="dot primarycolor"></div>
            <div className="dot primarycolor"></div>
            <div className="dot secondarycolor"></div>
            <div className="dot secondarycolor"></div>
            <div className="dot secondarycolor"></div>
            <div className="dot secondarycolor"></div>
            <div className="dot secondarycolor"></div>
            <div className="dot secondarycolor"></div>
            <div className="dot secondarycolor"></div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default Spinner;
